import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { sortOptionsAlphabetically } from 'utils/sortOptionsAlphabetically';
import { useSupportedLanguages } from 'utils/customHooks/useSupportedLanguages';
import {
  OpSelectOptionType,
  OpSelectSingleProps,
} from 'new-components/DLS/OpSelect/OpSelect';

import { OpFormSelect, OpFormSelectProps } from '../OpFormSelect/OpFormSelect';

type OpFormLanguageSelectorProps = Omit<
  OpFormSelectProps<string, OpSelectOptionType<string>>,
  'options' | 'filterSort' | 'mode' | 'value' | 'onChange'
> & {
  labelMode?: 'name' | 'nativeName';
  normalizeLanguageCode?: boolean;
  defaultValue?: string;
} & Pick<
    OpSelectSingleProps<string, OpSelectOptionType<string>>,
    'value' | 'onChange'
  >;

export const OpFormLanguageSelector = ({
  labelMode = 'name',
  normalizeLanguageCode = false,
  defaultValue,
  value,
  initialValue,
  ...props
}: OpFormLanguageSelectorProps) => {
  const { t } = useTranslation();
  const supportedLanguages = useSupportedLanguages();

  const options = useMemo(
    () =>
      supportedLanguages.map(({ code, name, nativeName }) => ({
        value: code,
        // `name` keeps labels aligned to the current UI locale, while
        // `nativeName` helps users find their language in its own script.
        label: labelMode === 'nativeName' ? nativeName : name,
      })),
    [labelMode, supportedLanguages],
  );

  const normalizedValue =
    normalizeLanguageCode && typeof value === 'string'
      ? value.split('-')[0]
      : value;

  const defaultInitialValue =
    normalizeLanguageCode && typeof defaultValue === 'string'
      ? defaultValue.split('-')[0]
      : defaultValue;
  const isControlled = value !== undefined;

  /**
   * Most OpForm usage should be Form-managed (`name` + `initialValues`/`initialValue`).
   * We still support controlled `value`/`onChange` to interop with legacy, unrefactored
   * FormWrapper-style code paths that pass external form state through OpForm fields.
   */
  return (
    <OpFormSelect<string, OpSelectOptionType<string>>
      {...props}
      {...(isControlled
        ? {
            // Controlled legacy usage should not also be bound as a Form field.
            // This avoids dual-control behavior where Form.Item can mask external values.
            name: undefined,
            value: normalizedValue,
          }
        : { initialValue: initialValue ?? defaultInitialValue })}
      placeholder={t('Select language')}
      options={options}
      filterSort={sortOptionsAlphabetically}
    />
  );
};
